'use client';

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useNotification, NotificationType } from 'src/context/notification';
import {
  AUTO_HIDE_MAX_MS,
  AUTO_HIDE_MIN_MS,
  AUTO_HIDE_PER_CHAR_MS,
} from './constants';

const severityStyles: Record<NotificationType['type'], string> = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  warning: 'bg-orange-500 text-white',
  info: 'bg-blue-600 text-white',
};

const Notification = () => {
  const { notification, setNotification } = useNotification();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (notification) {
      setVisible(true);
    }
  }, [notification]);

  // Keyed on the notification object so each new notification gets a full timer, even
  // when it replaces a visible one.
  useEffect(() => {
    if (!visible || !notification) return;

    const messageLength = notification.message?.length ?? 0;
    const autoHideMs = Math.min(
      Math.max(messageLength * AUTO_HIDE_PER_CHAR_MS, AUTO_HIDE_MIN_MS),
      AUTO_HIDE_MAX_MS
    );

    const timer = setTimeout(() => {
      setVisible(false);
      setNotification(null);
    }, autoHideMs);

    return () => clearTimeout(timer);
  }, [visible, notification, setNotification]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setNotification(null);
  }, [setNotification]);

  if (!visible || !notification) return null;

  const severity = notification.type || 'success';

  return createPortal(
    <div
      className="fixed top-10 right-5 z-[100000]"
      data-testid="notification-container"
    >
      <div
        className={twMerge(
          'flex max-w-lg items-center gap-3 rounded-lg px-4 py-2 shadow-lg',
          severityStyles[severity]
        )}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        data-testid="notification-alert"
      >
        <span
          className="min-w-0 break-words text-sm font-medium"
          data-testid="notification-message"
        >
          {notification.message}
        </span>
        <button
          type="button"
          onClick={handleClose}
          className="ml-2 flex-shrink-0 rounded-md p-1 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label="Close notification"
          data-testid="notification-close-button"
        >
          <Icon icon={faXmark} className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default Notification;
